import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import PanelLayout from '../layouts/PanelLayout';
import { categoryService } from '../../services/categoryService';
import ActionMenu from '../../components/common/ActionMenu';
import Loader from '../../components/Loader';
import Button from '../../components/common/Button';
import ConfirmModal from '../../components/common/ConfirmModal';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import Pagination from '../../components/common/Pagination';

const LIMIT = 10;

const SortIcon = ({ direction }) => {
   if (!direction)
      return (
         <svg
            className="w-3.5 h-3.5 opacity-40"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            viewBox="0 0 24 24"
         >
            <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4" />
         </svg>
      );
   return (
      <svg
         className="w-3.5 h-3.5"
         fill="none"
         stroke="currentColor"
         strokeWidth="2"
         viewBox="0 0 24 24"
      >
         {direction === 'asc' ? <path d="M5 15l7-7 7 7" /> : <path d="M19 9l-7 7-7-7" />}
      </svg>
   );
};

const Categories = () => {
   const [categories, setCategories] = useState([]);
   const [loading, setLoading] = useState(true);
   const [page, setPage] = useState(1);
   const [search, setSearch] = useState('');
   const [blogsSort, setBlogsSort] = useState(null); // null | 'asc' | 'desc'
   const [showForm, setShowForm] = useState(false);
   const [editingId, setEditingId] = useState(null);
   const [formData, setFormData] = useState({ name: '' });
   const [saving, setSaving] = useState(false);
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [deleting, setDeleting] = useState(false);

   useEffect(() => {
      fetchCategories();
   }, []);

   const fetchCategories = async () => {
      try {
         setLoading(true);
         const response = await categoryService.getAll(true);
         setCategories(response.data);
      } catch (error) {
         console.error('Error fetching categories:', error);
      } finally {
         setLoading(false);
      }
   };

   const handleSubmit = async (e) => {
      e.preventDefault();
      if (!formData.name.trim()) {
         toast.error('Category name is required');
         return;
      }

      try {
         setSaving(true);
         if (editingId) {
            await categoryService.update(editingId, formData);
            toast.success('Category updated successfully');
         } else {
            await categoryService.create(formData);
            toast.success('Category created successfully');
         }
         setShowForm(false);
         setEditingId(null);
         setFormData({ name: '' });
         setPage(1);
         fetchCategories();
      } catch (error) {
         console.error('Error saving category:', error);
         toast.error(error.response?.data?.message || 'Failed to save category');
      } finally {
         setSaving(false);
      }
   };

   const handleEdit = (category) => {
      setEditingId(category.id);
      setFormData({ name: category.name });
      setShowForm(true);
   };

   const handleDeleteClick = (category) => {
      if ((category.blogCount || 0) > 0) {
         toast.error(
            `Cannot delete "${category.name}". It has ${category.blogCount} blog(s) attached. Remove or reassign blogs first.`
         );
         return;
      }
      setDeleteTarget(category);
   };

   const handleDelete = async () => {
      if (!deleteTarget) return;
      try {
         setDeleting(true);
         await categoryService.delete(deleteTarget.id);
         toast.success('Category deleted successfully');
         setDeleteTarget(null);
         setPage(1);
         fetchCategories();
      } catch (error) {
         console.error('Error deleting category:', error);
         toast.error(error.response?.data?.message || 'Failed to delete category');
      } finally {
         setDeleting(false);
      }
   };

   const handleCancel = () => {
      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '' });
   };

   const handleBlogsSortToggle = () => {
      setBlogsSort((prev) => (prev === null ? 'asc' : prev === 'asc' ? 'desc' : null));
      setPage(1);
   };

   const handleSearchChange = (e) => {
      setSearch(e.target.value);
      setPage(1);
   };

   if (loading) {
      return (
         <PanelLayout>
            <Loader />
         </PanelLayout>
      );
   }

   const q = search.trim().toLowerCase();
   const filtered = q
      ? categories.filter(
           (c) => c.name.toLowerCase().includes(q) || c.slug.toLowerCase().includes(q)
        )
      : categories;

   const sorted =
      blogsSort === null
         ? filtered
         : [...filtered].sort((a, b) =>
              blogsSort === 'asc'
                 ? (a.blogCount || 0) - (b.blogCount || 0)
                 : (b.blogCount || 0) - (a.blogCount || 0)
           );

   const totalPages = Math.ceil(sorted.length / LIMIT);
   const paginatedCategories = sorted.slice((page - 1) * LIMIT, page * LIMIT);

   return (
      <PanelLayout>
         <div className="space-y-6">
            <div className="flex items-center justify-between">
               <div>
                  <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
                     Categories
                  </h1>
                  <p className="mt-2 text-gray-600 dark:text-gray-400">Manage blog categories</p>
               </div>
               <Button onClick={() => setShowForm(true)}>Create New Category</Button>
            </div>

            {/* Search */}
            <div className="max-w-sm">
               <Input
                  value={search}
                  onChange={handleSearchChange}
                  placeholder="Search by name or slug…"
               />
            </div>

            <div className="rounded-lg bg-white dark:bg-dark-secondary shadow-md overflow-hidden">
               <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                     <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Name
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Slug
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           <button
                              onClick={handleBlogsSortToggle}
                              className="flex items-center gap-1.5 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                           >
                              Blogs
                              <SortIcon direction={blogsSort} />
                           </button>
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                           Actions
                        </th>
                     </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-dark-secondary divide-y divide-gray-200 dark:divide-gray-700">
                     {paginatedCategories.length === 0 ? (
                        <tr>
                           <td
                              colSpan="4"
                              className="px-6 py-4 text-center text-gray-500 dark:text-gray-400"
                           >
                              No categories found
                           </td>
                        </tr>
                     ) : (
                        paginatedCategories.map((category) => (
                           <tr
                              key={category.id}
                              onClick={() => handleEdit(category)}
                              className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                           >
                              <td className="px-6 py-4 whitespace-nowrap">
                                 <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                    {category.name}
                                 </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                 <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {category.slug}
                                 </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                 <span className="text-sm text-gray-600 dark:text-gray-400">
                                    {category.blogCount || 0}
                                 </span>
                              </td>
                              <td
                                 className="px-6 py-4 whitespace-nowrap"
                                 onClick={(e) => e.stopPropagation()}
                              >
                                 <ActionMenu
                                    items={[
                                       { label: 'Edit', onClick: () => handleEdit(category) },
                                       {
                                          label: 'Delete',
                                          variant: 'danger',
                                          onClick: () => handleDeleteClick(category),
                                       },
                                    ]}
                                 />
                              </td>
                           </tr>
                        ))
                     )}
                  </tbody>
               </table>
            </div>

            <Pagination
               page={page}
               totalPages={totalPages}
               total={sorted.length}
               limit={LIMIT}
               onPageChange={setPage}
            />
         </div>

         <Modal
            isOpen={showForm}
            onClose={handleCancel}
            title={editingId ? 'Edit Category' : 'Create New Category'}
         >
            <form onSubmit={handleSubmit} className="space-y-4">
               <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     Name <span className="text-red-500">*</span>
                  </label>
                  <Input
                     value={formData.name}
                     onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                     placeholder="Category name"
                     required
                  />
               </div>
               <div className="flex justify-end gap-3">
                  <Button type="button" variant="secondary" size="sm" onClick={handleCancel}>
                     Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={saving}>
                     {saving ? 'Saving...' : editingId ? 'Update' : 'Create'}
                  </Button>
               </div>
            </form>
         </Modal>

         <ConfirmModal
            isOpen={!!deleteTarget}
            title="Delete Category"
            message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
            onConfirm={handleDelete}
            onCancel={() => setDeleteTarget(null)}
            loading={deleting}
         />
      </PanelLayout>
   );
};

export default Categories;
